import { IAccount, IImportJob, IOperationResultNoValue } from '@ViewModels';
import { css } from 'aphrodite';
import { makeObservable, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import {
	ErrorMessagesViewModelKey,
	IErrorMessageComponentProps,
	IUserSessionComponentProps,
	UserSessionViewModelKey,
} from '../../../models/AppState';
import { IEventLoggingComponentProps, withEventLogging } from '../../../models/Logging';
import { AccountImportsViewModel, AccountUserViewModel } from '../../../viewmodels/AdminViewModels';
import { Checkbox } from '../../../web/components/Checkbox';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../web/components/ConfirmationDialog';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { Modal } from '../../../web/components/Modal';
import { WarningIcon } from '../../../web/components/svgs/icons/WarningIcon';
import { baseStyleSheet } from '../../../web/styles/styles';
import { ImportJobUser } from '../ImportJobUser';
import {
	resolveRevertError,
	RevertResultModal,
	type RevertResultState,
	revertResultFromOperation,
} from '../RevertResultModal';
import { styleSheet } from './styles';

const RevertConfirmationOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isDestructive: true,
		representedObject: true,
		title: 'Revert',
	},
	{
		isCancel: true,
		representedObject: false,
		title: 'Cancel',
	},
];

interface IProps extends IUserSessionComponentProps, IErrorMessageComponentProps, IEventLoggingComponentProps {
	account: IAccount;
	className?: string;
	isOpen: boolean;
	onRequestClose?(result?: unknown, canceled?: boolean): void;
	userVM: AccountUserViewModel;
}

interface IState {
	forceJobId?: string;
	revertResultState: RevertResultState | null;
	selectedJobId?: string;
	showForceConfirmation: boolean;
	showRevertConfirmation: boolean;
	showRevertedJobs: boolean;
}

class _ImportHistory extends React.Component<IProps, IState> {
	private mImportsViewModel: AccountImportsViewModel;
	constructor(props: IProps) {
		super(props);

		makeObservable<_ImportHistory, 'mImportsViewModel'>(this, {
			mImportsViewModel: observable.ref,
		});

		this.mImportsViewModel = new AccountImportsViewModel(props.userSession, props.account);
		this.state = {
			forceJobId: '',
			revertResultState: null,
			selectedJobId: '',
			showForceConfirmation: false,
			showRevertConfirmation: false,
			showRevertedJobs: false,
		};
	}

	public componentDidMount() {
		this.loadImportHistory();
	}

	public componentDidUpdate(prevProps: IProps) {
		if (this.props.isOpen !== prevProps.isOpen && this.props.isOpen) {
			this.loadImportHistory();
		}
	}

	public render() {
		const { userVM, onRequestClose } = this.props;
		const { selectedJobId, forceJobId, showRevertedJobs } = this.state;
		const systemJobs = this.mImportsViewModel.accountImports;

		return (
			<Modal
				className={`import-history ${this.props.className || ''}`}
				isOpen={!!this.props.isOpen}
				onRequestClose={onRequestClose}
				useDefaultHeader={true}
			>
				<ConfirmationDialog
					icon={<WarningIcon />}
					modalProps={{
						isOpen: !!this.state.showRevertConfirmation,
						onRequestClose: this.onRevertConfirmationRequestClose,
					}}
					options={RevertConfirmationOptions}
					title='Reverting this import will delete any contacts created by the import. Are you sure?'
				/>
				<ConfirmationDialog
					icon={<WarningIcon />}
					modalProps={{
						isOpen: !!this.state.showForceConfirmation,
						onRequestClose: this.onForceRevertConfirmationRequestClose,
					}}
					options={RevertConfirmationOptions}
					title='Force reverting this import will permanently delete any contacts created during the import, even if they were not part of the imported file. Are you absolutely sure?'
				/>
				<RevertResultModal
					isOpen={this.state.revertResultState !== null}
					onRequestClose={this.onRevertResultClose}
					state={this.state.revertResultState}
				/>
				<div className={css(styleSheet.container)}>
					{this.mImportsViewModel.isLoading ? (
						<LoadingSpinner type='large' />
					) : (
						<>
							<div className={css(styleSheet.title, baseStyleSheet.truncateText)}>{userVM?.name || ''}</div>
							<div className={css(styleSheet.sectionTitle)}>Import History</div>
							<div className={css(styleSheet.revertCheckbox)}>
								<Checkbox
									id='import-reverted-modal-checkbox'
									onChange={this.onShowRevertedChanged}
									checked={showRevertedJobs}
								>
									<span>Show reverted imports</span>
								</Checkbox>
							</div>
							<div className={css(styleSheet.jobList)}>
								{!!systemJobs && systemJobs.length > 0 ? (
									systemJobs.map(systemJob => {
										return (
											<ImportJobUser
												isReverting={
													this.mImportsViewModel.isReverting &&
													(selectedJobId === systemJob.id || forceJobId === systemJob.id)
												}
												key={systemJob.id}
												systemJob={systemJob}
												revertImport={this.revertImport(systemJob.id)}
												onForceRevertImport={
													forceJobId === systemJob.id ? this.onForceRevertImport(systemJob.id) : null
												}
												onViewRevertSummary={this.onViewRevertSummary(systemJob)}
											/>
										);
									})
								) : (
									<div className={css(styleSheet.noneFound)}>No Imports</div>
								)}
							</div>
						</>
					)}
				</div>
			</Modal>
		);
	}

	private loadImportHistory = () => {
		const { userVM } = this.props;

		if (userVM) {
			this.mImportsViewModel.loadImportsByUser(userVM.id).catch((err: IOperationResultNoValue) => {
				const { errorMessages } = this.props;

				errorMessages.pushApiError(err);
			});
		}
	};

	private revertImport = (selectedJobId: string) => () => {
		this.setState({
			selectedJobId,
			showRevertConfirmation: true,
		});
	};

	private onForceRevertImport = (forceJobId: string) => () => {
		this.setState({
			forceJobId,
			showForceConfirmation: true,
		});
	};

	private onRevertConfirmationRequestClose = (result?: IConfirmationDialogOption<boolean>, canceled?: boolean) => {
		const { selectedJobId } = this.state;
		if (!!result && !canceled && !!result.representedObject && !!selectedJobId) {
			this.mImportsViewModel
				.revertImport(selectedJobId)
				.then(revert => {
					this.setState({
						revertResultState: revertResultFromOperation(revert),
						selectedJobId: '',
						forceJobId: '',
					});
				})
				.catch((err: IOperationResultNoValue) => {
					const resolution = resolveRevertError(err);
					if (resolution.showInModal) {
						this.setState({
							revertResultState: resolution.state,
							forceJobId: selectedJobId,
							selectedJobId: '',
						});
					} else {
						const { errorMessages } = this.props;
						errorMessages.pushApiError(err);
						this.setState({
							forceJobId: selectedJobId,
							selectedJobId: '',
						});
					}
				});
		}
		this.setState({ showRevertConfirmation: false });
	};

	private onForceRevertConfirmationRequestClose = (result?: IConfirmationDialogOption<boolean>, canceled?: boolean) => {
		const { forceJobId } = this.state;
		if (!!result && !canceled && !!result.representedObject && !!forceJobId) {
			this.mImportsViewModel
				.forceRevertImport(forceJobId)
				.then(revert => {
					this.setState({ revertResultState: revertResultFromOperation(revert) });
				})
				.catch((err: IOperationResultNoValue) => {
					const resolution = resolveRevertError(err);
					if (resolution.showInModal) {
						this.setState({ revertResultState: resolution.state });
					} else {
						const { errorMessages } = this.props;
						errorMessages.pushApiError(err);
					}
				});
		}
		this.setState({ forceJobId: '', showForceConfirmation: false });
	};

	private onRevertResultClose = () => {
		this.setState({ revertResultState: null });
	};

	private onViewRevertSummary = (systemJob: IImportJob) => () => {
		if (!systemJob.revertSummary) return;
		this.setState({
			revertResultState: revertResultFromOperation({ success: true, value: systemJob.revertSummary }),
		});
	};

	private onShowRevertedChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ showRevertedJobs: e.target.checked });
		this.mImportsViewModel.loadImports(e.target.checked)?.catch(err => {
			const { errorMessages, logApiError } = this.props;

			logApiError('Show-Reverted-Error', err);

			errorMessages.pushApiError(err);
		});
	};
}

const ImportHistoryWithObserver = observer(_ImportHistory);
const ImportHistoryWithLogging = withEventLogging(ImportHistoryWithObserver);
export const ImportHistory = inject(UserSessionViewModelKey, ErrorMessagesViewModelKey)(ImportHistoryWithLogging);
